import React, { useCallback, useEffect, useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { ErrorBoundary, LazyComponent } from '@fiverr-private/ui_utils';
import type {
    PricingConfiguration,
    PricingContext as PricingContextType,
} from '@fiverr-private/price_upfront_components';
import {
    LazyDrawerProps,
    OPEN_DRAWER_EVENT_NAME,
    setIsSourcingDrawerMounted,
} from '@fiverr-private/expert_sourcing_domain_selection_drawer_trigger';
import { Currency } from '../../types';
import { SourcingDomainSelectionDrawerProps } from '.';

declare global {
    interface WindowEventMap {
        openSourcingDrawer: CustomEvent<LazyDrawerProps>;
    }
}

interface LazySourcingDomainSelectionDrawerProps {
    currency: Currency;
    isPriceUpfrontEnabled?: boolean;
    priceUpfrontConfiguration?: PricingConfiguration;
    priceUpfrontContext?: PricingContextType;
}

const SourcingDomainSelectionDrawerLazyImport = () =>
    import(/* webpackChunkName: "SourcingDomainSelectionDrawer" */ '.');

const LazySourcingDomainSelectionDrawer: React.FC<LazySourcingDomainSelectionDrawerProps> = ({
    currency,
    isPriceUpfrontEnabled,
    priceUpfrontConfiguration,
    priceUpfrontContext,
}) => {
    const [drawerProps, setDrawerProps] = useState<LazyDrawerProps>({
        isOpen: false,
        source: '',
    });

    const setIsDrawerOpen = useCallback(
        (isOpen: boolean) => {
            if (drawerProps.isOpen !== isOpen) {
                setDrawerProps((prevProps) => ({ ...prevProps, isOpen }));
            }
        },
        [drawerProps]
    );

    useEffect(() => {
        const handleDrawerOpenEvent = async (event: CustomEvent<LazyDrawerProps>) => {
            setDrawerProps((previousProps) => ({
                ...previousProps,
                ...event.detail,
                isOpen: true,
            }));
        };

        addEventListener(OPEN_DRAWER_EVENT_NAME, handleDrawerOpenEvent);
        setIsSourcingDrawerMounted(true);

        return () => {
            removeEventListener(OPEN_DRAWER_EVENT_NAME, handleDrawerOpenEvent);
            setIsSourcingDrawerMounted(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onError = (error) => {
        logger.error(error, {
            description: 'Failed to lazy load SourcingDomainSelectionDrawer',
        });
    };

    const resolvedIsPriceUpfrontEnabled = drawerProps.pricingData?.isPriceUpfrontEnabled ?? isPriceUpfrontEnabled;
    const resolvedConfiguration = (drawerProps.pricingData?.priceUpfrontConfiguration ?? priceUpfrontConfiguration) as
        | PricingConfiguration
        | undefined;
    const resolvedContext = (drawerProps.pricingData?.priceUpfrontContext ?? priceUpfrontContext) as
        | PricingContextType
        | undefined;

    return (
        <ErrorBoundary onError={onError}>
            <LazyComponent<SourcingDomainSelectionDrawerProps>
                lazyImport={SourcingDomainSelectionDrawerLazyImport}
                shouldImport={drawerProps.isOpen}
                onError={onError}
                componentProps={{
                    isOpen: drawerProps.isOpen,
                    setIsOpen: setIsDrawerOpen,
                    enableGoBack: drawerProps.enableGoBack,
                    source: drawerProps.source,
                    currency,
                    isPriceUpfrontEnabled: resolvedIsPriceUpfrontEnabled,
                    priceUpfrontConfiguration: resolvedConfiguration,
                    priceUpfrontContext: resolvedContext,
                }}
                beforeLoad={() => drawerProps.setIsLoading?.(true)}
                afterLoad={() => drawerProps.setIsLoading?.(false)}
            />
        </ErrorBoundary>
    );
};

export default LazySourcingDomainSelectionDrawer;
